/**
 * SmartHub.js
 * Unified favorites hub - aggregates all saved items from different sources
 * 
 * Categories:
 * - Restaurant Picks: Favorites from eating out
 * - Home Recipes: Saved recipes from Smart Search
 * - Logged Favorites: Manually saved meals
 * - Healthy Swaps: Home versions of restaurant dishes
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Utensils, ChefHat, BookmarkCheck, RefreshCw, 
  ChevronDown, ChevronUp, Star, Clock, Flame, X, Plus
} from 'lucide-react';
import { brand } from '../../styles/brand';

const { colors, gradients, typography, radius, spacing } = brand;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY CONFIG
// ═══════════════════════════════════════════════════════════════════════════════
const CATEGORIES = {
  restaurant: {
    key: 'restaurant',
    label: 'Restaurant Swaps',
    icon: Utensils,
    color: '#4CAF50',
    emptyText: 'Save a healthy restaurant swap from Smart Swaps'
  },
  home_recipe: {
    key: 'home_recipe',
    label: 'Home Recipes',
    icon: ChefHat,
    color: '#667eea',
    emptyText: 'Save a recipe from Smart Swaps to add it here'
  },
  logged_meal: {
    key: 'logged_meal',
    label: 'Logged Favorites',
    icon: BookmarkCheck,
    color: '#FF9800',
    emptyText: 'Favorite a logged meal to add it here'
  },
  healthy_swap: {
    key: 'healthy_swap',
    label: 'Healthy Swaps',
    icon: RefreshCw,
    color: '#9C27B0',
    emptyText: 'Save a healthy swap recipe to add it here'
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// FAVORITE CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const FavoriteCard = ({ item, category, onLog, onRemove, isLogging }) => {
  const config = CATEGORIES[category];
  const Icon = config.icon;
  
  return (
    <div
      data-testid={`favorite-card-${item.id}`}
      style={{
        padding: spacing.s3,
        background: colors.panel,
        borderRadius: radius.md,
        border: `1px solid ${colors.stroke}`,
        display: 'flex',
        alignItems: 'center',
        gap: spacing.s3
      }}
    >
      {/* Icon */}
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: radius.sm,
        background: `${config.color}20`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <Icon size={20} style={{ color: config.color }} />
      </div>
      
      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: typography.body.fontSize,
          fontWeight: '600',
          fontFamily: typography.fontFamily,
          color: colors.text,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {item.name}
        </div>
        
        {/* Meta info */}
        <div style={{
          fontSize: '11px',
          fontFamily: typography.fontFamily,
          color: colors.textSecondary,
          display: 'flex',
          alignItems: 'center',
          gap: spacing.s2,
          marginTop: '2px'
        }}>
          {category === 'restaurant' && item.meta?.restaurant_name && (
            <span>{item.meta.restaurant_name}</span>
          )}
          {category === 'home_recipe' && item.meta?.cuisine_type && (
            <span>{item.meta.cuisine_type}</span>
          )}
          {category === 'healthy_swap' && item.meta?.original_dish && (
            <span>Swap for: {item.meta.original_dish}</span>
          )}
          {item.use_count > 0 && (
            <span style={{ color: colors.mutedText }}>
              · {item.use_count}x logged
            </span>
          )}
        </div>
        
        {/* Macros */}
        <div style={{
          display: 'flex',
          gap: spacing.s3,
          marginTop: spacing.s2,
          fontSize: '11px',
          fontFamily: typography.fontFamily
        }}>
          <span style={{ color: colors.success, fontWeight: '600' }}>
            {item.calories} kcal
          </span>
          <span style={{ color: '#81C784' }}>
            {item.protein}g P
          </span>
          <span style={{ color: '#FFD54F' }}>
            {item.carbs}g C
          </span>
          <span style={{ color: '#FF8A65' }}>
            {item.fat}g F
          </span>
        </div>
      </div>
      
      {/* Actions */}
      <div style={{ display: 'flex', gap: spacing.s2, flexShrink: 0 }}>
        <button
          data-testid={`log-favorite-${item.id}`}
          onClick={() => onLog(item, category)}
          disabled={isLogging}
          style={{
            padding: `${spacing.s2} ${spacing.s3}`,
            background: gradients.primary,
            border: 'none',
            borderRadius: radius.sm,
            color: colors.text,
            fontFamily: typography.fontFamily,
            fontSize: typography.bodySmall.fontSize,
            fontWeight: '600',
            cursor: isLogging ? 'wait' : 'pointer',
            opacity: isLogging ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            minHeight: '36px'
          }}
        >
          <Plus size={14} />
          Log
        </button>
        
        <button
          data-testid={`remove-favorite-${item.id}`}
          onClick={() => onRemove(item, category)}
          style={{
            padding: spacing.s2,
            background: 'rgba(255,255,255,0.05)',
            border: `1px solid ${colors.stroke}`,
            borderRadius: radius.sm,
            color: colors.textSecondary,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '36px',
            minHeight: '36px'
          }}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// CATEGORY SECTION COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const CategorySection = ({ category, items, onLog, onRemove, isLogging }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const config = CATEGORIES[category];
  const Icon = config.icon;
  
  if (items.length === 0) return null;
  
  return (
    <div 
      data-testid={`category-section-${category}`}
      style={{ marginBottom: spacing.s4 }}
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: spacing.s3,
          background: `${config.color}10`,
          border: `1px solid ${config.color}30`,
          borderRadius: radius.md,
          cursor: 'pointer',
          marginBottom: isExpanded ? spacing.s3 : 0
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.s2 }}>
          <Icon size={18} style={{ color: config.color }} />
          <span style={{
            fontSize: typography.body.fontSize,
            fontWeight: '600',
            fontFamily: typography.fontFamily,
            color: colors.text
          }}>
            {config.label}
          </span>
          <span style={{
            fontSize: typography.bodySmall.fontSize,
            fontFamily: typography.fontFamily,
            color: colors.textSecondary,
            background: 'rgba(255,255,255,0.1)',
            padding: '2px 8px',
            borderRadius: radius.full
          }}>
            {items.length}
          </span>
        </div>
        {isExpanded ? <ChevronUp size={18} color={colors.textSecondary} /> : <ChevronDown size={18} color={colors.textSecondary} />}
      </button>
      
      {/* Items */}
      {isExpanded && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.s2 }}>
          {items.map(item => (
            <FavoriteCard
              key={item.id}
              item={item}
              category={category}
              onLog={onLog}
              onRemove={onRemove}
              isLogging={isLogging === item.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const SmartHub = ({ userId, onMealLogged, onClose }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [favorites, setFavorites] = useState({
    restaurant: [],
    home_recipe: [],
    logged_meal: [],
    healthy_swap: [],
    total_count: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [loggingId, setLoggingId] = useState(null);
  const [error, setError] = useState(null);
  
  // ─────────────────────────────────────────────────────────────────────────
  // FETCH FAVORITES
  // ─────────────────────────────────────────────────────────────────────────
  const fetchFavorites = useCallback(async () => {
    if (!userId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/smart-hub/favorites/${userId}`);
      if (!response.ok) throw new Error('Failed to load favorites');
      
      const data = await response.json();
      setFavorites(data);
    } catch (err) {
      console.error('[SmartHub] Error fetching favorites:', err);
      setError('Unable to load favorites');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);
  
  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // LOG FAVORITE
  // ─────────────────────────────────────────────────────────────────────────
  const handleLog = async (item, category) => {
    setLoggingId(item.id);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/smart-hub/favorites/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          favorite_id: item.id,
          source_type: category
        })
      });
      
      if (!response.ok) throw new Error('Failed to log meal');
      
      const data = await response.json();
      
      // Notify parent
      if (onMealLogged) {
        onMealLogged(data.logged, item.name);
      }
      
      // Refresh to update use counts
      fetchFavorites();
      
    } catch (err) {
      console.error('[SmartHub] Error logging favorite:', err);
    } finally {
      setLoggingId(null);
    }
  };
  
  // ─────────────────────────────────────────────────────────────────────────
  // REMOVE FAVORITE
  // ─────────────────────────────────────────────────────────────────────────
  const handleRemove = async (item, category) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/smart-hub/favorites/${category}/${item.id}?user_id=${userId}`,
        { method: 'DELETE' }
      );
      
      if (!response.ok) throw new Error('Failed to remove');
      
      // Update local state
      setFavorites(prev => ({
        ...prev,
        [category]: prev[category].filter(f => f.id !== item.id),
        total_count: prev.total_count - 1
      }));
      
    } catch (err) {
      console.error('[SmartHub] Error removing favorite:', err);
    }
  };
  
  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  
  // Don't render if no favorites and not loading
  if (!isLoading && favorites.total_count === 0) {
    return null;
  }
  
  return (
    <div 
      data-testid="smart-hub"
      style={{
        background: colors.panel,
        borderRadius: radius.lg,
        border: `1px solid ${colors.stroke}`,
        overflow: 'hidden',
        marginBottom: spacing.s4
      }}
    >
      {/* Collapsible Header - Always visible */}
      <button
        data-testid="smart-hub-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: '100%',
          padding: spacing.s4,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.s3 }}>
          <Star size={20} style={{ color: '#FFD700' }} />
          <div style={{ textAlign: 'left' }}>
            <h2 style={{
              fontSize: typography.body.fontSize,
              fontWeight: '600',
              fontFamily: typography.fontFamily,
              color: colors.text,
              margin: 0
            }}>
              Smart Hub
            </h2>
            <p style={{
              fontSize: typography.bodySmall.fontSize,
              fontFamily: typography.fontFamily,
              color: colors.textSecondary,
              margin: 0
            }}>
              {isLoading ? 'Loading...' : `${favorites.total_count} favorite${favorites.total_count !== 1 ? 's' : ''} — one-tap log`}
            </p>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.s2 }}>
          {!isLoading && favorites.total_count > 0 && (
            <span style={{
              fontSize: typography.bodySmall.fontSize,
              fontFamily: typography.fontFamily,
              color: colors.accent,
              background: `${colors.accent}20`,
              padding: '2px 8px',
              borderRadius: radius.full
            }}>
              {favorites.total_count}
            </span>
          )}
          {isExpanded ? <ChevronUp size={20} color={colors.textSecondary} /> : <ChevronDown size={20} color={colors.textSecondary} />}
        </div>
      </button>
      
      {/* Expanded Content */}
      {isExpanded && (
        <div style={{ 
          padding: spacing.s4, 
          paddingTop: 0,
          borderTop: `1px solid ${colors.stroke}`
        }}>
          {isLoading ? (
            <div style={{
              padding: spacing.s4,
              textAlign: 'center',
              color: colors.textSecondary
            }}>
              Loading favorites...
            </div>
          ) : error ? (
            <div style={{
              padding: spacing.s4,
              textAlign: 'center',
              color: '#f44336'
            }}>
              {error}
            </div>
          ) : (
            <>
              {/* Category Sections */}
              <CategorySection
                category="restaurant"
                items={favorites.restaurant}
                onLog={handleLog}
                onRemove={handleRemove}
                isLogging={loggingId}
              />
              <CategorySection
                category="home_recipe"
                items={favorites.home_recipe}
                onLog={handleLog}
                onRemove={handleRemove}
                isLogging={loggingId}
              />
              <CategorySection
                category="healthy_swap"
                items={favorites.healthy_swap}
                onLog={handleLog}
                onRemove={handleRemove}
                isLogging={loggingId}
              />
              <CategorySection
                category="logged_meal"
                items={favorites.logged_meal}
                onLog={handleLog}
                onRemove={handleRemove}
                isLogging={loggingId}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SmartHub;
